import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { ArrowLeft, Download, FileText, Calendar, User, FolderOpen, Loader2, Eye, Edit2, Trash2, Upload } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { nl, de, enUS } from 'date-fns/locale';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Get token from localStorage
const getToken = () => localStorage.getItem('carer-token');

const DocumentDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const { isAdmin } = useAuth();
  const [docData, setDocData] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({ name: '', description: '', file: null });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  // Progress tracking state
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [estimatedTime, setEstimatedTime] = useState(null);
  const downloadStartTime = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchDocument();
    fetchCategories();
  }, [id]);

  const fetchDocument = async () => {
    try {
      const res = await axios.get(`${API}/documents/${id}`);
      setDocData(res.data);
      setEditForm({ name: res.data.name, description: res.data.description || '', file: null });
    } catch (e) {
      toast.error(language === 'nl' ? 'Document niet gevonden' : language === 'de' ? 'Dokument nicht gefunden' : 'Document not found');
      navigate('/documents');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API}/categories`);
      setCategories(res.data);
    } catch (e) {
      console.error('Failed to fetch categories:', e);
    }
  };

  const handleDownload = async () => {
    try {
      const res = await axios.get(`${API}/documents/${id}/download`, {
        responseType: 'blob',
        timeout: 120000, // 2 minute timeout for large files
        onDownloadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const progress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
            setDownloadProgress(progress);
          }
        }
      });
      
      // Verify we received valid data
      if (!res.data || res.data.size === 0) {
        throw new Error('Empty response received');
      }
      
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = window.document.createElement('a');
      link.href = url;
      link.setAttribute('download', docData.original_filename || docData.name);
      window.document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setDownloadProgress(0);
    } catch (e) {
      console.error('Download error:', e);
      const errorMsg = e.code === 'ECONNABORTED' 
        ? (language === 'nl' ? 'Download timeout - probeer opnieuw' : language === 'de' ? 'Download-Timeout - erneut versuchen' : 'Download timeout - please retry')
        : (language === 'nl' ? 'Download mislukt' : language === 'de' ? 'Download fehlgeschlagen' : 'Download failed');
      toast.error(errorMsg);
      setDownloadProgress(0);
    }
  };

  const handlePreview = () => {
    // Use direct embed URL with token for iframe - avoids blob URL issues
    const token = getToken();
    if (!token) {
      toast.error(language === 'nl' ? 'Niet ingelogd' : language === 'de' ? 'Nicht angemeldet' : 'Not logged in');
      return;
    }
    
    // Build direct URL to embed endpoint
    const embedUrl = `${API}/documents/${id}/embed?token=${encodeURIComponent(token)}`;
    setPreviewUrl(embedUrl);
    setShowPreview(true);
    setDownloadProgress(100);
  };

  const formatEstimatedTime = (seconds) => {
    if (seconds === null || seconds === undefined) return '';
    if (seconds < 60) {
      return language === 'nl' 
        ? `Nog ${seconds} seconden...` 
        : language === 'de' 
        ? `Noch ${seconds} Sekunden...` 
        : `${seconds} seconds remaining...`;
    }
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return language === 'nl' 
      ? `Nog ${minutes}:${secs.toString().padStart(2, '0')} minuten...` 
      : language === 'de' 
      ? `Noch ${minutes}:${secs.toString().padStart(2, '0')} Minuten...` 
      : `${minutes}:${secs.toString().padStart(2, '0')} remaining...`;
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('name', editForm.name);
      formData.append('description', editForm.description);
      
      // Add file if selected
      if (editForm.file) {
        formData.append('file', editForm.file);
      }
      
      const res = await axios.put(`${API}/documents/${id}`, formData);
      setDocData(res.data);
      setEditOpen(false);
      setEditForm({ ...editForm, file: null });
      toast.success(language === 'nl' ? 'Document bijgewerkt' : language === 'de' ? 'Dokument aktualisiert' : 'Document updated');
    } catch (e) {
      toast.error(language === 'nl' ? 'Bijwerken mislukt' : language === 'de' ? 'Aktualisierung fehlgeschlagen' : 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    const confirmMsg = language === 'nl' 
      ? 'Weet je zeker dat je dit document wilt verwijderen? Dit kan niet ongedaan worden gemaakt.'
      : language === 'de'
      ? 'Sind Sie sicher, dass Sie dieses Dokument löschen möchten? Dies kann nicht rückgängig gemacht werden.'
      : 'Are you sure you want to delete this document? This cannot be undone.';
    
    if (!window.confirm(confirmMsg)) return;
    
    setDeleting(true);
    try {
      await axios.delete(`${API}/documents/${id}`);
      toast.success(language === 'nl' ? 'Document verwijderd' : language === 'de' ? 'Dokument gelöscht' : 'Document deleted');
      navigate('/documents');
    } catch (e) {
      toast.error(language === 'nl' ? 'Verwijderen mislukt' : language === 'de' ? 'Löschen fehlgeschlagen' : 'Delete failed');
      setDeleting(false);
    }
  };

  const getCategoryName = (catId) => {
    const cat = categories.find(c => c.id === catId);
    if (!cat) return catId;
    return language === 'nl' ? cat.name_nl : language === 'de' ? cat.name_de : (cat.name_en || cat.name_nl);
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getFileTypeColor = (type) => {
    const colors = {
      pdf: 'text-red-500 bg-red-100 dark:bg-red-500/10',
      doc: 'text-blue-500 bg-blue-100 dark:bg-blue-500/10',
      docx: 'text-blue-500 bg-blue-100 dark:bg-blue-500/10',
      xls: 'text-green-500 bg-green-100 dark:bg-green-500/10',
      xlsx: 'text-green-500 bg-green-100 dark:bg-green-500/10',
      png: 'text-purple-500 bg-purple-100 dark:bg-purple-500/10',
      jpg: 'text-purple-500 bg-purple-100 dark:bg-purple-500/10',
      jpeg: 'text-purple-500 bg-purple-100 dark:bg-purple-500/10',
    };
    return colors[type] || 'text-gray-500 bg-gray-100 dark:bg-zinc-500/10';
  };

  const canPreview = docData && ['pdf', 'png', 'jpg', 'jpeg', 'gif', 'webp'].includes(docData.file_type);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!docData) {
    return null;
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="document-detail-page">
      {/* Back button */}
      <Link to="/documents">
        <Button variant="ghost" className="text-muted-foreground hover:text-foreground">
          <ArrowLeft className="w-4 h-4 mr-2" />
          {language === 'nl' ? 'Terug naar documenten' : language === 'de' ? 'Zurück zu Dokumenten' : 'Back to documents'}
        </Button>
      </Link>

      {/* Document info */}
      <Card className="bg-card border-border">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row gap-6">
            {/* File icon */}
            <div className={`p-6 rounded-xl ${getFileTypeColor(docData.file_type)} self-start`}>
              <FileText className="w-16 h-16" />
            </div>

            {/* Info */}
            <div className="flex-1 space-y-4">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <h1 className="text-2xl font-bold text-foreground">{docData.name}</h1>
                {isAdmin && (
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setEditOpen(true)}
                      className="border-border text-foreground hover:bg-accent"
                      data-testid="edit-document-btn"
                    >
                      <Edit2 className="w-4 h-4 mr-2" />
                      {t('common.edit')}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleDelete}
                      disabled={deleting}
                      className="border-red-300 text-red-600 hover:bg-red-50 dark:border-red-800 dark:text-red-400 dark:hover:bg-red-900/20"
                      data-testid="delete-document-btn"
                    >
                      {deleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                      {t('common.delete')}
                    </Button>
                  </div>
                )}
              </div>
              
              {docData.description && (
                <p className="text-muted-foreground">{docData.description}</p>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <FolderOpen className="w-4 h-4" />
                  <span>{getCategoryName(docData.category)}</span>
                </div>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <FileText className="w-4 h-4" />
                  <span>{docData.file_type?.toUpperCase()} • {formatFileSize(docData.file_size)}</span>
                </div>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <User className="w-4 h-4" />
                  <span>{docData.uploaded_by_name}</span>
                </div>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Calendar className="w-4 h-4" />
                  <span>
                    {formatDistanceToNow(new Date(docData.created_at), { 
                      addSuffix: true,
                      locale: language === 'nl' ? nl : language === 'de' ? de : enUS 
                    })}
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-2 text-muted-foreground text-sm">
                <Download className="w-4 h-4" />
                <span>{docData.download_count} {language === 'nl' ? 'downloads' : language === 'de' ? 'Downloads' : 'downloads'}</span>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4 flex-wrap">
                {canPreview && (
                  <Button 
                    variant="outline" 
                    className="border-border text-foreground hover:bg-accent"
                    onClick={handlePreview}
                    data-testid="preview-btn"
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    {t('docs.preview')}
                  </Button>
                )}
                <Button 
                  className="bg-secondary hover:bg-secondary/90 text-secondary-foreground btn-glow-orange"
                  onClick={handleDownload}
                  data-testid="download-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  {t('docs.download')}
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Preview */}
      {showPreview && (
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-foreground">{t('docs.preview')}</h2>
              <Button 
                variant="ghost" 
                size="sm"
                className="text-muted-foreground hover:text-foreground"
                onClick={() => {
                  setShowPreview(false);
                  setPreviewUrl(null);
                  setDownloadProgress(0);
                }}
              >
                {language === 'nl' ? 'Sluiten' : language === 'de' ? 'Schließen' : 'Close'}
              </Button>
            </div>
            {previewLoading ? (
              <div className="flex flex-col items-center justify-center py-16 space-y-6">
                <Loader2 className="w-10 h-10 animate-spin text-primary" />
                <div className="w-full max-w-md space-y-3">
                  <div className="flex justify-between text-sm text-muted-foreground">
                    <span>
                      {language === 'nl' ? 'Document laden...' : language === 'de' ? 'Dokument wird geladen...' : 'Loading document...'}
                    </span>
                    <span className="font-medium text-foreground">{downloadProgress}%</span>
                  </div>
                  <Progress value={downloadProgress} className="h-2" />
                  {estimatedTime !== null && (
                    <p className="text-sm text-muted-foreground text-center">
                      {formatEstimatedTime(estimatedTime)}
                    </p>
                  )}
                </div>
              </div>
            ) : previewUrl ? (
              docData.file_type === 'pdf' ? (
                <div className="space-y-4">
                  <object
                    data={previewUrl}
                    type="application/pdf"
                    className="w-full h-[600px] rounded-lg border border-border"
                  >
                    <div className="flex flex-col items-center justify-center h-[600px] bg-muted/30 rounded-lg border border-border">
                      <FileText className="w-16 h-16 text-muted-foreground mb-4" />
                      <p className="text-muted-foreground mb-4">
                        {language === 'nl' 
                          ? 'PDF kan niet worden weergegeven in browser' 
                          : language === 'de' 
                          ? 'PDF kann im Browser nicht angezeigt werden' 
                          : 'PDF cannot be displayed in browser'}
                      </p>
                      <a 
                        href={previewUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        {language === 'nl' ? 'Open in nieuw tabblad' : language === 'de' ? 'In neuem Tab öffnen' : 'Open in new tab'}
                      </a>
                    </div>
                  </object>
                </div>
              ) : (
                <img 
                  src={previewUrl} 
                  alt={docData.name}
                  className="max-w-full h-auto rounded-lg"
                />
              )
            ) : null}
          </CardContent>
        </Card>
      )}

      {/* Edit Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="text-foreground">
              {language === 'nl' ? 'Document bewerken' : language === 'de' ? 'Dokument bearbeiten' : 'Edit document'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEdit} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-foreground">{t('docs.name')}</Label>
              <Input
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                className="bg-background border-border text-foreground"
                required
                data-testid="edit-doc-name"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-foreground">{t('docs.description')}</Label>
              <Textarea
                value={editForm.description}
                onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                className="bg-background border-border text-foreground"
                rows={4}
                data-testid="edit-doc-description"
              />
            </div>
            
            {/* File replacement section */}
            <div className="space-y-2">
              <Label className="text-foreground">
                {language === 'nl' ? 'Bestand vervangen' : language === 'de' ? 'Datei ersetzen' : 'Replace file'}
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  ref={fileInputRef}
                  type="file"
                  onChange={(e) => setEditForm({ ...editForm, file: e.target.files?.[0] || null })}
                  className="bg-background border-border text-foreground"
                  data-testid="edit-doc-file"
                />
              </div>
              {editForm.file && (
                <p className="text-sm text-green-600 dark:text-green-400">
                  {language === 'nl' ? 'Nieuw bestand geselecteerd: ' : language === 'de' ? 'Neue Datei ausgewählt: ' : 'New file selected: '}
                  {editForm.file.name}
                </p>
              )}
              <p className="text-xs text-muted-foreground">
                {language === 'nl' 
                  ? 'Laat leeg om het huidige bestand te behouden' 
                  : language === 'de' 
                  ? 'Leer lassen, um die aktuelle Datei zu behalten' 
                  : 'Leave empty to keep the current file'}
              </p>
            </div>
            
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setEditOpen(false)} className="border-border text-foreground">
                {t('common.cancel')}
              </Button>
              <Button type="submit" className="bg-primary hover:bg-primary/90" disabled={saving} data-testid="edit-doc-submit">
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {t('common.save')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DocumentDetailPage;
